import type { ReactNode } from "react";
import type { VariantProps } from "class-variance-authority";

import { Button, buttonVariants } from "./button";
import { Tooltip, TooltipPopup, TooltipTrigger } from "./tooltip";

export interface RowActionIconButtonProps {
  /**
   * Accessible name for the button AND the hover tooltip text. This must be
   * the same plain-language wording a text button would have used (e.g.
   * "Stop", "Destroy") — never abbreviate it just because it is invisible.
   */
  readonly label: string;
  readonly icon: ReactNode;
  readonly onClick: () => void;
  readonly disabled?: boolean;
  readonly variant?: VariantProps<typeof buttonVariants>["variant"];
  readonly size?: VariantProps<typeof buttonVariants>["size"];
  readonly tooltipSide?: "top" | "bottom" | "left" | "right";
}

/**
 * Icon-only button for repeated per-row action clusters (list/table rows that
 * render the same 2+ lifecycle/destructive actions on every row — e.g.
 * Settings > Connections "Agent runtimes"). Bakes in the accessible name and
 * hover tooltip so a row of icon buttons never ships without a screen-reader
 * name. See docs/design/row-action-buttons.md for the convention this backs,
 * and apps/web/src/rowActionButtonGuard.test.ts for the guard that enforces
 * registered row-action surfaces keep using it.
 */
/**
 * The coarse-pointer floor, applied here rather than inherited.
 *
 * `settingsControlDensity.ts` raises controls to `--spacing-touch` below `md`
 * with `max-md:[&_[data-slot=button]]:min-h-touch`, and that rule has never
 * once matched one of these: Base UI's `TooltipTrigger` merges its own
 * `data-slot="tooltip-trigger"` over the `data-slot="button"` the Button sets,
 * so every icon-only row action falls out of the selector. Measured in
 * Chromium at 320px: 28px, against a documented 48px minimum, on the controls
 * whose entire job is to be tapped.
 *
 * Bounded at `max-md:` for the reason `settingsControlDensity.ts` gives —
 * Forge's touch/pointer boundary is 768px, not `sm`'s 640px — and written as
 * `min-*` so the size variants below `md` are raised, never shrunk.
 */
const ROW_ACTION_TOUCH_CLASS = "max-md:min-h-touch max-md:min-w-touch max-md:rounded-xl";

export function RowActionIconButton({
  label,
  icon,
  onClick,
  disabled = false,
  variant = "outline",
  size = "icon-xs",
  tooltipSide = "top",
}: RowActionIconButtonProps) {
  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <Button
            size={size}
            variant={variant}
            aria-label={label}
            className={ROW_ACTION_TOUCH_CLASS}
            disabled={disabled}
            onClick={onClick}
          >
            {icon}
          </Button>
        }
      />
      <TooltipPopup side={tooltipSide}>{label}</TooltipPopup>
    </Tooltip>
  );
}
